/**
 * 光传产品清单相关 API。
 *
 * 类型定义与接口路径来源于 Apifox「设计中心」项目。
 */
import { getApiRequest } from '@/utils'
import type { IRes } from '@/types'
import type { AxiosRequestConfig } from 'axios'

const request = getApiRequest()

/** 光传 PDF 产品清单展示类型。 */
export enum OpticalPdfProductListType {
  /** Consolidated List */
  CONSOLIDATED = 1,
  /** Site Breakdown List */
  SITE_BREAKDOWN = 2,
}

/** 光传产品清单请求上下文。 */
export interface OpticalCatalogContext {
  /** 项目 ID。 */
  projectId: number
  /** 区域节点 ID（UUID）；也用于表示后端约定的 All sites。 */
  areaId: string
}

/** 光传产品的模块选项。 */
export interface OpticalProductModel {
  /** 模块值。 */
  value?: number
  /** 模块名称。 */
  name?: string
  /** 是否选中。 */
  selected?: boolean
}

/** 光传产品清单明细。 */
export interface OpticalProductSettlementDetail {
  /** 结算产品 ID。 */
  id?: number
  /** 产品 ID。 */
  productId?: number
  /** 产品标记。 */
  productMark?: number
  /** 产品型号名称。 */
  productModelName?: string
  /** 产品名称。 */
  productName?: string
  /** 产品图片。 */
  productImage?: string
  /** 产品数量。 */
  quantity?: number
  /** 产品权重。 */
  weight?: number
  /** 产品价格。 */
  productPrice?: string
  /** 产品模块总价。 */
  productItemTotalPrice?: string
  /** 产品分类 ID。 */
  categoryId?: number
  /** 产品分类名称。 */
  categoryName?: string
  /** 产品状态。 */
  status?: number
  /** 是否展示重置按钮。 */
  isRefresh?: boolean
  /** 是否支持替换。 */
  isReplace?: boolean
  /** 是否支持新增。 */
  isAdd?: boolean
  /** 是否为拓扑图产品。 */
  isAuto?: boolean
  /** 产品标签列表。 */
  productLabelList?: string[]
  /** AMPCON 模块选项组。 */
  modelGroupList?: OpticalProductModel[][]
  /** 产品模块组。 */
  moduleProductItem?: OpticalProductSettlementDetail[]
}

/** 光传产品清单数据。 */
export interface OpticalProductSettlementVO {
  /** 总金额。 */
  totalAmount?: string
  /** 是否允许整体重置。 */
  isTotalRefresh?: boolean
  /** 产品清单。 */
  item?: OpticalProductSettlementDetail[]
  /** 是否为分享项目：1 是，0 否。 */
  isShared?: number
  /** 是否可编辑：1 是，0 否。 */
  isEdit?: number
  /** Apifox 定义的基准站点 languageId，不等同于区域节点 areaId。 */
  baseSite?: number
}

/** 重置光传产品清单参数；不传 id 时重置整个清单。 */
export interface OpticalCatalogResetParams extends OpticalCatalogContext {
  /** 结算产品 ID。 */
  id?: number
}

/** 新增到光传产品清单的产品。 */
export interface OpticalCatalogAddProductItem {
  /** 产品 ID。 */
  productId: number
  /** 产品标记：1 线缆、2 模块、3 AMPCON、0 其他。 */
  projectMark: number
}

/** 新增光传产品参数。 */
export interface OpticalCatalogAddProductDTO extends OpticalCatalogContext {
  /** 待新增的产品列表。 */
  productList: OpticalCatalogAddProductItem[]
}

/** 编辑或替换光传产品参数。 */
export interface OpticalCatalogEditProductDTO {
  /** 新产品 ID。 */
  productId: number
  /** 结算产品 ID。 */
  id: number
  /** 区域节点 ID（UUID）。 */
  areaId?: string
}

/** 修改光传产品数量参数。 */
export interface OpticalCatalogProductNumberDTO {
  /** 产品数量。 */
  productNumber: number
  /** 结算产品 ID。 */
  id: number
}

/** 删除光传产品参数。 */
export interface OpticalCatalogRemoveProductParams extends OpticalCatalogContext {
  /** 产品 ID。 */
  productId: number
}

/** 保存光传产品勾选状态的请求参数。 */
export interface SaveOpticalProductSelectionDTO extends OpticalCatalogContext {
  /** 勾选的产品 ID 列表。 */
  productIds: number[]
}

/** 光传产品勾选状态。 */
export interface OpticalProductSelectionVO {
  /** 勾选的产品 ID 列表。 */
  productIds?: number[]
  /** 是否已经手动操作过勾选。 */
  operatedSelect?: boolean
}

/** 光传产品清单 PDF 导出请求参数。 */
export interface ProductListOpticalPdfDTO {
  /** 项目 ID。 */
  projectId: number
  /** 当前站点下拉选项的区域节点 ID（UUID）。 */
  areaId: string
  /** 产品清单展示类型：1-Consolidated List；2-Site Breakdown List。 */
  type?: OpticalPdfProductListType
  /** 勾选的产品 ID 列表。 */
  productIdList?: number[]
}

/** 查询指定项目和区域的光传产品清单。 */
export function getOpticalChecklist(params: OpticalCatalogContext, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalProductSettlementVO>>('/design/settlement/optical/checklist', {
    ...config,
    params,
  })
}

/** 重置整个光传产品清单或其中一个结算产品。 */
export function resetOpticalChecklist(params: OpticalCatalogResetParams, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalProductSettlementVO>>('/design/settlement/optical/reset', {
    ...config,
    params,
  })
}

/** 向光传产品清单新增产品。 */
export function addOpticalProduct(data: OpticalCatalogAddProductDTO, config?: AxiosRequestConfig) {
  return request.post<IRes<string>>('/design/settlement/optical/add-product', data, config)
}

/** 编辑或替换光传产品。 */
export function editOpticalProduct(data: OpticalCatalogEditProductDTO, config?: AxiosRequestConfig) {
  return request.put<IRes<string>>('/design/settlement/optical/edit-product', data, config)
}

/** 修改光传产品数量。 */
export function updateOpticalProductNumber(data: OpticalCatalogProductNumberDTO, config?: AxiosRequestConfig) {
  return request.put<IRes<string>>('/design/settlement/optical/product-number', data, config)
}

/** 从光传产品清单中删除产品。 */
export function removeOpticalProduct(params: OpticalCatalogRemoveProductParams, config?: AxiosRequestConfig) {
  return request.delete<IRes<string>>('/design/settlement/optical/remove-product', {
    ...config,
    params,
  })
}

/** 保存指定区域下的光传产品勾选状态。 */
export function saveOpticalProductSelection(data: SaveOpticalProductSelectionDTO, config?: AxiosRequestConfig) {
  return request.post<IRes<string>>('/design/settlement/optical/save-selected', data, config)
}

/** 查询指定区域下的光传产品勾选状态。 */
export function queryOpticalProductSelection(params: OpticalCatalogContext, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalProductSelectionVO>>('/design/settlement/optical/query-selected', {
    ...config,
    params,
  })
}

/** 导出光传产品清单 PDF。 */
export function exportProductListOpticalPdf(data: ProductListOpticalPdfDTO, config?: AxiosRequestConfig) {
  return request.post<Blob>('/design/export/productList/optical-pdf', data, {
    ...config,
    responseType: 'blob',
  })
}
